import ConfirmModal from '@/components/ConfirmModal';
import { consentsSelector } from '@/features/configuration/configurationSelector';
import { deleteProfileLoadingSelector } from '@/features/loading/loadingSelectors';
import { deleteProfile } from '@/features/profile/profileActions';
import { profileDataSelector } from '@/features/profile/profileSelectors';
import { useAuth } from '@/hooks/useAuth';
import { TransAlert } from '@/i18n/trans/alert';
import { TransText } from '@/i18n/trans/text';
import { useDispatch, useSelector } from '@/store/utils';
import { cn } from '@/utils/cn';
import { sanitizeMarkup } from '@/utils/sanitizeMarkup';
import {
  Accordion,
  Button,
  Card,
  Checkbox,
  Divider,
  Icons,
  Input,
  Label,
  Typography,
  typographyVariants,
} from '@fleet/webshop-ui';
import { useCallback, useMemo, useState, type FC } from 'react';
import { useFieldArray, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

interface ConsentFormValues {
  consents: {
    id: number;
    code?: string;
    isMandatory: boolean;
    description?: string;
    checked: boolean;
  }[];
}

const AccountDetailsSettings: FC = () => {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const consents = useSelector(consentsSelector);
  const profile = useSelector(profileDataSelector);
  const isDeletingProfile = useSelector(deleteProfileLoadingSelector);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

  const handleDeleteAccount = useCallback(async () => {
    try {
      await dispatch(deleteProfile()).unwrap();
      toast.success(<TransAlert i18nKey="accountSuccessfullyDeleted" />);
      await logout();
    } catch {
      toast.error(<TransAlert i18nKey="accountDeletionFailed" />);
    }
  }, [dispatch, logout]);

  const defaultValues = useMemo<ConsentFormValues>(
    () => ({
      consents:
        consents?.items?.map(({ id, isMandatory, description, code }) => ({
          id,
          isMandatory,
          description,
          code,
          checked: isMandatory,
        })) ?? [],
    }),
    [consents]
  );

  const { control, handleSubmit, watch } = useForm<ConsentFormValues>({
    defaultValues,
  });

  const { fields, update } = useFieldArray({
    control,
    name: 'consents',
  });

  const watchedConsents = watch('consents');
  const hasChanges = useMemo(
    () =>
      watchedConsents.some(
        (consent, index) =>
          consent.checked !== defaultValues.consents[index]?.checked
      ),
    [watchedConsents, defaultValues.consents]
  );

  const onSubmit = useCallback((data: ConsentFormValues) => {
    const consentDecisions = data.consents.map((consent) => ({
      code: consent.code,
      decisionId: consent.checked
        ? 'CONSENT_DECISION.ACCEPTED'
        : 'CONSENT_DECISION.NOT_ACCEPTED',
    }));

    // TODO: Replace with API call when endpoint is available
    console.log('Consent decisions:', consentDecisions);
  }, []);

  return (
    <div className="flex h-fit w-full flex-col gap-4 text-dark">
      <Card className="w-full rounded-lg px-6 pb-6 pt-4">
        <div className="flex flex-col gap-4">
          <div className="flex items-center gap-3">
            <Icons.lock className="h-6 w-6" />
            <Typography variant="heading2" as="h2">
              <TransText i18nKey="loginDetails" />
            </Typography>
          </div>

          <Divider />

          <div className="flex flex-col gap-4">
            <Typography variant="body1-bold">
              <TransText i18nKey="logInWithEmailAndPassword" />
            </Typography>

            <div className="flex flex-col gap-1">
              <Label
                htmlFor="account-email"
                className={cn(
                  typographyVariants({ variant: 'body2' }),
                  'text-neutral'
                )}
              >
                <TransText i18nKey="email" />
              </Label>
              <Input
                id="account-email"
                data-testid="account-email-input"
                placeholder={profile?.email ?? ''}
                readOnly
                disabled
              />
            </div>

            <Button
              variant="ghost"
              className="w-fit gap-2"
              startAdornment={<Icons.edit className="h-5 w-5" />}
              onClick={() => navigate('/account/change-password')}
              data-testid="change-password-button"
            >
              <TransText i18nKey="changePassword" />
            </Button>
          </div>
        </div>
      </Card>

      <Card>
        <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-4">
          <div className="flex items-center gap-3">
            <Icons.userCheck className="h-6 w-6" />
            <Typography variant="heading2" as="h2">
              <TransText i18nKey="consents" />
            </Typography>
          </div>

          <Divider />

          <div className="flex flex-col gap-4" data-testid="account-consents">
            {fields.map((consent, index) => {
              const markup = consent.description || '';

              return (
                <div key={consent.id} className="flex items-center gap-3">
                  <Checkbox
                    size="lg"
                    isSelected={consent.checked}
                    isDisabled={consent.isMandatory}
                    onChange={(checked) =>
                      update(index, { ...consent, checked })
                    }
                  />
                  <div className="flex flex-wrap items-center gap-0.5">
                    {markup ? (
                      <Typography
                        dangerouslySetInnerHTML={{
                          __html: sanitizeMarkup(markup),
                        }}
                        variant="body1"
                        className={cn('text-dark prose-a:text-blue-600')}
                      />
                    ) : (
                      <Typography variant="body1" className="text-dark">
                        <TransText i18nKey="undefinedConsentDescription" />
                      </Typography>
                    )}
                    {!consent.isMandatory && (
                      <Typography variant="body2" className="text-neutral">
                        (<TransText i18nKey="optional" />)
                      </Typography>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          <div>
            <Button
              type="submit"
              variant="secondary"
              className="w-fit gap-2"
              data-testid="save-consents-button"
              startAdornment={<Icons.check className="h-5 w-5" />}
              isDisabled={!hasChanges}
            >
              <TransText i18nKey="save" />
            </Button>
          </div>
        </form>
      </Card>

      <Card className="flex flex-col gap-4">
        <div className="flex items-center gap-2">
          <Icons.infoCircle className="h-6 w-6" />
          <Typography variant="heading2" as="h2">
            <TransText i18nKey="deleteAccount" />
          </Typography>
        </div>

        <Divider />

        <Accordion
          classNames={{
            root: 'border-b-0',
            content: 'flex flex-col gap-4 pt-4',
          }}
          trigger={
            <Typography variant="body1-bold">
              <TransText i18nKey="permanentlyDeleteAccount" />
            </Typography>
          }
          data-testid="delete-account-accordion"
        >
          <Typography variant="body1" className="text-dark">
            <TransText i18nKey="deleteAccountDescription" />
          </Typography>

          <Button
            variant="destructive"
            className="w-fit gap-2"
            startAdornment={<Icons.trash className="h-5 w-5" />}
            onClick={() => setIsDeleteModalOpen(true)}
            data-testid="delete-account-button"
          >
            <TransText i18nKey="deleteAccount" />
          </Button>
        </Accordion>
      </Card>

      <ConfirmModal
        open={isDeleteModalOpen}
        setOpen={setIsDeleteModalOpen}
        title={
          <Typography variant="subtitle">
            <TransText i18nKey="permanentlyDeleteAccountConfirm" />
          </Typography>
        }
        description={
          <div className="flex flex-col gap-4">
            <Typography variant="body1">
              <TransText i18nKey="deleteAccountModalDescription" />
            </Typography>
            <Divider className="-mx-4 w-auto" />
          </div>
        }
        cancelLabel="cancel"
        confirmLabel="deleteAccount"
        confirmVariant="destructive"
        confirmStartAdornment={<Icons.trash className="h-5 w-5" />}
        isConfirmLoading={isDeletingProfile}
        onConfirm={handleDeleteAccount}
      />

      <Button
        variant="secondary"
        className="mt-4 w-fit gap-2"
        startAdornment={<Icons.logOut className="h-5 w-5" />}
        onClick={logout}
        data-testid="logout-button"
      >
        <TransText i18nKey="logOut" />
      </Button>
    </div>
  );
};

export default AccountDetailsSettings;
